import type { ComponentType, ReactElement } from "react";
import type {
	ActionLifecycle,
	AnyComponentInterceptor,
	ItemContainerConfig,
	ColumnCustomizerFn,
	CustomComponent,
	CustomizationsConfig,
	DateValidationProps,
	GuestPageProps,
	HeroComponentProps,
	LayoutAppBarProps,
	LayoutBreadcrumbsProps,
	LayoutFooterProps,
	LayoutNavigationDrawerProps,
	NavigationItem,
	PageCustomization,
	SubThemeHook,
	TableRowHighlightConfig,
	VisualPropertyOverrides,
	VisualPropertySet,
} from "@judo/model-api";
import type { PageDefinition, VisualElement } from "@judo/model-api";
import {
	type ReactNode,
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	useSyncExternalStore,
} from "react";
import type { DataStoreState } from "../store";
import { useDataStoreOptional } from "./data-context";
import { usePageContextOptional } from "./page-context";

// ============================================
// CONTEXT VALUE TYPE
// ============================================

/**
 * @internal Context value for the customizations system.
 *
 * This is a framework-internal contract. Developers access customizations
 * through the generated `createCustomizations()` factory, not this context directly.
 */
export interface CustomizationsContextType {
	/** Internal version counter for HMR / change detection */
	version: number;
	/** @internal Get the page customization bundle (keyed by page's xmi:id, resolved from the model element) */
	getPageCustomization(pageSourceId: string): PageCustomization | undefined;
	/** @internal Get the custom component (keyed by element's xmi:id, resolved from the model element) */
	getComponent(elementSourceId: string): CustomComponent | undefined;
	/** Get the component interceptor for an element @type */
	getComponentInterceptor(elementType: string): AnyComponentInterceptor | undefined;
	/** Get the sub-theme provider for a named sub-theme */
	getSubThemeProvider(subThemeName: string): SubThemeHook | undefined;
	/** Get the redirect handler component for the /_redirect route */
	getRedirectHandler(): ComponentType | undefined;
	/** Get custom routes to inject into the router */
	getCustomRoutes(): Array<{ path: string; element: ReactElement }> | undefined;
	/** Get the menu customizer function */
	getMenuCustomizer(): ((items: NavigationItem[]) => NavigationItem[]) | undefined;
	/** Get the footer text (static or dynamic) */
	getFooterText(): string | (() => string) | undefined;
	/** Get the custom hero component for the AppBar */
	getHeroComponent(): ComponentType<HeroComponentProps> | undefined;
	/** Get the settings page component */
	getSettingsPage(): ComponentType | undefined;
	/** Get the custom guest page component (for supportGuestAccess mode) */
	getGuestComponent(): ComponentType<GuestPageProps> | undefined;
	/** Get the custom AppBar layout slot component */
	getAppBarComponent(): ComponentType<LayoutAppBarProps> | undefined;
	/** Get the custom NavigationDrawer layout slot component */
	getNavigationDrawerComponent(): ComponentType<LayoutNavigationDrawerProps> | undefined;
	/** Get the custom Footer layout slot component */
	getFooterComponent(): ComponentType<LayoutFooterProps> | undefined;
	/** Get the custom Breadcrumbs layout slot component */
	getBreadcrumbsComponent(): ComponentType<LayoutBreadcrumbsProps> | undefined;
}

const CustomizationsContext = createContext<CustomizationsContextType | null>(null);

// ============================================
// HOOKS
// ============================================

/**
 * Hook to access customizations context.
 * @throws Error if used outside CustomizationsProvider
 */
export function useCustomizations(): CustomizationsContextType {
	const context = useContext(CustomizationsContext);
	if (!context) {
		throw new Error("useCustomizations must be used within CustomizationsProvider");
	}
	return context;
}

/**
 * Hook to access customizations context (optional).
 * Returns null if no CustomizationsProvider is in the tree.
 */
export function useCustomizationsOptional(): CustomizationsContextType | null {
	return useContext(CustomizationsContext);
}

/**
 * Returns the custom component for a visual element, if registered.
 *
 * @internal This is a framework-internal hook used by `VisualElementRenderer`.
 * Developers register component overrides via the type-safe `createCustomizations()`
 * factory using human-readable names — never raw sourceIds.
 *
 * @param element - The VisualElement model object (identity resolved from element["xmi:id"])
 * @returns The custom component or null
 */
export function useComponentOverride(element: VisualElement): CustomComponent | null {
	const customizations = useCustomizationsOptional();
	if (!customizations) return null;

	const sourceId = element["xmi:id"];
	if (!sourceId) return null;

	return customizations.getComponent(sourceId) ?? null;
}

/**
 * Returns an intercepted component for a visual element based on its @type.
 *
 * @internal This is a framework-internal hook used by `VisualElementRenderer`.
 * Developers register interceptors via the type-safe `createCustomizations()`
 * factory using the `componentInterceptors` property with human-readable @type keys
 * (e.g., 'TextInput', 'Table').
 *
 * Called after `useComponentOverride` (per-element override takes priority over type interceptor).
 *
 * @param element - The VisualElement model object
 * @returns The intercepted custom component or null
 */
export function useComponentInterceptor(element: VisualElement): CustomComponent | null {
	const customizations = useCustomizationsOptional();
	const pageContext = usePageContextOptional();
	if (!customizations || !pageContext) return null;

	const elementType = element["@type"];
	if (!elementType) return null;

	const interceptor = customizations.getComponentInterceptor(elementType);
	if (!interceptor) return null;

	return interceptor(element, pageContext.pageDefinition) ?? null;
}

/**
 * Returns the sub-theme provider hook for an element's `subTheme` property.
 *
 * @internal This is a framework-internal hook used by `SubThemeWrapper`.
 * Developers register sub-theme providers via the type-safe `createCustomizations()`
 * factory using the `subThemeProviders` property with human-readable sub-theme names.
 *
 * @param element - The VisualElement model object (must have `subTheme` property)
 * @returns The SubThemeHook or null if not registered
 */
export function useSubThemeProvider(element: VisualElement): SubThemeHook | null {
	const customizations = useCustomizationsOptional();
	if (!customizations) return null;

	const subThemeName = element.subTheme;
	if (!subThemeName) return null;

	return customizations.getSubThemeProvider(subThemeName) ?? null;
}

/**
 * Returns the action lifecycle overrides for a specific action on a page.
 * Used by the action dispatch system to compose overrides with built-in handlers.
 *
 * @param page - The PageDefinition
 * @param action - The Action to check
 * @returns Partial ActionLifecycle or null
 */
export function usePageActionOverrides(
	page: PageDefinition | undefined,
	action: { "xmi:id"?: string } | undefined
): Partial<ActionLifecycle> | null {
	const customizations = useCustomizationsOptional();
	const pageContext = usePageContextOptional();

	// Use provided page or fall back to current page context
	const effectivePage = page ?? pageContext?.pageDefinition;

	if (!customizations || !effectivePage || !action) return null;

	const pageSourceId = effectivePage["xmi:id"];
	if (!pageSourceId) return null;

	const pageCustomization = customizations.getPageCustomization(pageSourceId);
	if (!pageCustomization?.actions) return null;

	// Note: the hook is called at the provider level (PageActionOverridesProvider),
	// not here. This function is called with the already-resolved overrides.
	// However, for direct usage we can resolve inline.
	const actionSourceId = action["xmi:id"];
	if (!actionSourceId) return null;

	return null; // Resolved at provider level, not here
}

/**
 * Returns visual property overrides for an element on the current page.
 *
 * @internal This is a framework-internal hook used by `useVisualBinding` and component renderers.
 * Developers provide visual property overrides via the type-safe `createCustomizations()`
 * factory using human-readable element names within page scopes.
 *
 * @param element - The VisualElement model object
 * @returns Partial VisualPropertySet or null
 */
export function useVisualPropertyOverrides(element: VisualElement): Partial<VisualPropertySet> | null {
	const ctx = useContext(VisualPropertiesContext);
	if (!ctx) return null;

	const sourceId = element["xmi:id"];
	if (!sourceId) return null;

	return ctx[sourceId] ?? null;
}

/**
 * Returns the typeahead provider for a text input element on the current page.
 *
 * @internal This is a framework-internal hook called by input renderers.
 * Developers register typeahead providers via the type-safe `createCustomizations()`
 * factory using human-readable element names.
 *
 * @param elementSourceId - The element's xmi:id (resolved from the model object, not developer-supplied)
 * @returns The typeahead provider function, or undefined if not registered
 */
export function useTypeaheadProvider(
	elementSourceId: string | undefined
): ((text: string) => Promise<string[]>) | undefined {
	const customizations = useCustomizationsOptional();
	const pageContext = usePageContextOptional();
	if (!customizations || !pageContext || !elementSourceId) return undefined;

	const pageSourceId = pageContext.pageDefinition?.["xmi:id"];
	if (!pageSourceId) return undefined;

	return customizations.getPageCustomization(pageSourceId)?.typeaheadProviders?.[elementSourceId];
}

/**
 * Returns the row highlighting configuration for a table element on the current page.
 *
 * @internal This is a framework-internal hook called by `TableRenderer`.
 * Developers register row highlighting via the type-safe `createCustomizations()`
 * factory using human-readable table element names.
 *
 * @param elementSourceId - The element's xmi:id (resolved from the model object, not developer-supplied)
 * @returns Array of highlight configs, or undefined if not registered
 */
export function useTableRowHighlighting(elementSourceId: string | undefined): TableRowHighlightConfig[] | undefined {
	const customizations = useCustomizationsOptional();
	const pageContext = usePageContextOptional();
	if (!customizations || !pageContext || !elementSourceId) return undefined;

	const pageSourceId = pageContext.pageDefinition?.["xmi:id"];
	if (!pageSourceId) return undefined;

	return customizations.getPageCustomization(pageSourceId)?.tableRowHighlighting?.[elementSourceId];
}

/**
 * Returns a wrapped enumeration option filter for an enum input element on the current page.
 *
 * @internal This is a framework-internal hook called by `InputRenderer`.
 * Developers register enum filters via the type-safe `createCustomizations()`
 * factory using human-readable element names.
 *
 * @param elementSourceId - The element's xmi:id (resolved from the model object, not developer-supplied)
 * @returns A function that takes options and returns filtered options, or undefined if not registered
 */
export function useEnumOptionFilter(
	elementSourceId: string | undefined
): ((options: Array<{ value: string; label: string }>) => Array<{ value: string; label: string }>) | undefined {
	const customizations = useCustomizationsOptional();
	const pageContext = usePageContextOptional();
	const store = useDataStoreOptional();

	const pageSourceId =
		customizations && pageContext && elementSourceId ? pageContext.pageDefinition?.["xmi:id"] : undefined;
	const filterFn = pageSourceId
		? customizations?.getPageCustomization(pageSourceId)?.enumOptionFilter?.[elementSourceId!]
		: undefined;

	// Subscribe to transfer data changes when a filter is configured.
	// When no filter, selector always returns undefined (no extra re-renders).
	const transferId = pageContext?.transferId ?? "";
	const hasFilter = !!filterFn;

	const selector = useCallback(
		(state: DataStoreState) => {
			if (!hasFilter) return undefined;
			return (state.transfers.get(transferId)?.data as Record<string, unknown> | undefined) ?? undefined;
		},
		[transferId, hasFilter]
	);

	const subscribe = useCallback(
		(cb: () => void) => (store ? store.subscribe(selector, cb) : () => {}),
		[store, selector]
	);

	const getSnapshot = useCallback(() => (store ? store.getSnapshot(selector) : undefined), [store, selector]);

	const entityData = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

	// Memoize wrapper to avoid re-creating on every render when filter + data haven't changed
	return useMemo(() => {
		if (!filterFn) return undefined;
		return (options: Array<{ value: string; label: string }>) => filterFn(entityData, options);
	}, [filterFn, entityData]);
}

/**
 * Returns resolved date validation props for a date/datetime input element on the current page.
 *
 * @internal This is a framework-internal hook called by date input renderers.
 * Developers register date validation via the type-safe `createCustomizations()`
 * factory using human-readable element names.
 *
 * @param elementSourceId - The element's xmi:id (resolved from the model object, not developer-supplied)
 * @returns Resolved date validation props, or undefined if not registered
 */
export function useDateValidationProps(elementSourceId: string | undefined): DateValidationProps | undefined {
	const customizations = useCustomizationsOptional();
	const pageContext = usePageContextOptional();
	const store = useDataStoreOptional();

	const pageSourceId =
		customizations && pageContext && elementSourceId ? pageContext.pageDefinition?.["xmi:id"] : undefined;
	const validationFn = pageSourceId
		? customizations?.getPageCustomization(pageSourceId)?.dateValidationProps?.[elementSourceId!]
		: undefined;

	// Subscribe to transfer data changes when a validation fn is configured.
	const transferId = pageContext?.transferId ?? "";
	const hasValidation = !!validationFn;

	const selector = useCallback(
		(state: DataStoreState) => {
			if (!hasValidation) return undefined;
			return (state.transfers.get(transferId)?.data as Record<string, unknown> | undefined) ?? undefined;
		},
		[transferId, hasValidation]
	);

	const subscribe = useCallback(
		(cb: () => void) => (store ? store.subscribe(selector, cb) : () => {}),
		[store, selector]
	);

	const getSnapshot = useCallback(() => (store ? store.getSnapshot(selector) : undefined), [store, selector]);

	const entityData = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

	return useMemo(() => {
		if (!validationFn) return undefined;
		return validationFn(entityData);
	}, [validationFn, entityData]);
}

/**
 * Returns all column customizer functions for the current page.
 *
 * @internal This is a framework-internal hook called by `TableRenderer`.
 * Developers register column customizers via the type-safe `createCustomizations()`
 * factory using human-readable column element names.
 *
 * @returns Record of column xmi:id → customizer function, or undefined if not registered
 */
export function useColumnCustomizers(): Record<string, ColumnCustomizerFn> | undefined {
	const customizations = useCustomizationsOptional();
	const pageContext = usePageContextOptional();
	if (!customizations || !pageContext) return undefined;

	const pageSourceId = pageContext.pageDefinition?.["xmi:id"];
	if (!pageSourceId) return undefined;

	return customizations.getPageCustomization(pageSourceId)?.columnCustomizers;
}

/**
 * Returns the item container configuration for a specific table element.
 *
 * @internal This is a framework-internal hook called by `TableRenderer`.
 * Developers register item container configs via the type-safe `createCustomizations()`
 * factory using human-readable table element names.
 *
 * @param elementSourceId - The element's xmi:id (resolved from the model object, not developer-supplied)
 * @returns Item container config or undefined if not registered
 */
export function useItemContainerConfig(elementSourceId: string | undefined): ItemContainerConfig | undefined {
	const customizations = useCustomizationsOptional();
	const pageContext = usePageContextOptional();
	if (!customizations || !pageContext || !elementSourceId) return undefined;

	const pageSourceId = pageContext.pageDefinition?.["xmi:id"];
	if (!pageSourceId) return undefined;

	return customizations.getPageCustomization(pageSourceId)?.itemContainerConfigs?.[elementSourceId];
}

// ============================================
// PAGE ACTION OVERRIDES CONTEXT
// ============================================

/**
 * @internal Resolved action overrides for the current page.
 * Key: action xmi:id (resolved internally), Value: lifecycle overrides
 */
const PageActionOverridesContext = createContext<Record<string, Partial<ActionLifecycle>> | null>(null);

/**
 * @internal Hook to get the resolved action overrides for the current page.
 * Returns all action overrides keyed by action xmi:id.
 */
export function useResolvedPageActionOverrides(): Record<string, Partial<ActionLifecycle>> | null {
	return useContext(PageActionOverridesContext);
}

/**
 * @internal Get lifecycle overrides for a specific action.
 * The actionSourceId is the action's xmi:id, resolved from the Action model object
 * (not developer-supplied).
 */
export function useActionLifecycleOverrides(actionSourceId: string | undefined): Partial<ActionLifecycle> | null {
	const overrides = useContext(PageActionOverridesContext);
	if (!overrides || !actionSourceId) return null;
	return overrides[actionSourceId] ?? null;
}

/**
 * Provider that calls the registered page action hook and provides
 * the resolved overrides to descendants.
 * Must be rendered inside PageProvider.
 */
export function PageActionOverridesProvider({ children }: { children: ReactNode }) {
	const customizations = useCustomizationsOptional();
	const pageContext = usePageContextOptional();

	const pageSourceId = pageContext?.pageDefinition?.["xmi:id"];
	const actionsHook = pageSourceId ? customizations?.getPageCustomization(pageSourceId)?.actions : undefined;

	// Call the hook (always, to respect rules of hooks)
	// If no hook registered, use a no-op that returns empty
	const overrides = actionsHook ? actionsHook() : null;

	return <PageActionOverridesContext.Provider value={overrides}>{children}</PageActionOverridesContext.Provider>;
}

// ============================================
// VISUAL PROPERTIES CONTEXT
// ============================================

const VisualPropertiesContext = createContext<VisualPropertyOverrides | null>(null);

/**
 * Provider that calls the registered visual properties hook and provides
 * the resolved overrides to descendants.
 * Must be rendered inside PageProvider.
 */
export function VisualPropertiesProvider({ children }: { children: ReactNode }) {
	const customizations = useCustomizationsOptional();
	const pageContext = usePageContextOptional();

	const pageSourceId = pageContext?.pageDefinition?.["xmi:id"];
	const visualHook = pageSourceId ? customizations?.getPageCustomization(pageSourceId)?.visualProperties : undefined;

	// Call the hook (always, to respect rules of hooks)
	const overrides = visualHook ? visualHook() : null;

	return <VisualPropertiesContext.Provider value={overrides}>{children}</VisualPropertiesContext.Provider>;
}

// ============================================
// CUSTOMIZATIONS PROVIDER
// ============================================

/**
 * Props for CustomizationsProvider.
 */
export interface CustomizationsProviderProps {
	children: ReactNode;
	/** The customizations config (from createCustomizations() or manually built) */
	customizations?: CustomizationsConfig | null;
}

/**
 * Provider for the customizations system.
 *
 * Stores the config in a ref and uses a version counter for HMR support.
 * When the customizations object identity changes (e.g., during HMR),
 * the version bumps and all consumers re-render with new values.
 *
 * @example
 * ```tsx
 * <CustomizationsProvider customizations={myCustomizations}>
 *   <JudoRuntime modelSource="/models/app.model" />
 * </CustomizationsProvider>
 * ```
 */
export function CustomizationsProvider({ children, customizations }: CustomizationsProviderProps) {
	const configRef = useRef(customizations ?? null);
	const [version, setVersion] = useState(0);

	// Update ref + bump version when customizations object identity changes (HMR support)
	useEffect(() => {
		configRef.current = customizations ?? null;
		setVersion((v) => v + 1);
	}, [customizations]);

	const value = useMemo<CustomizationsContextType>(
		() => ({
			version,
			getPageCustomization: (id: string) => configRef.current?.pages?.[id],
			getComponent: (id: string) => configRef.current?.components?.[id],
			getComponentInterceptor: (elementType: string) =>
				(configRef.current?.componentInterceptors as Record<string, AnyComponentInterceptor> | undefined)?.[
					elementType
				],
			getSubThemeProvider: (name: string) => configRef.current?.subThemeProviders?.[name],
			getRedirectHandler: () => configRef.current?.redirectHandler,
			getCustomRoutes: () => configRef.current?.customRoutes,
			getMenuCustomizer: () => configRef.current?.menuCustomizer,
			getFooterText: () => configRef.current?.footerText,
			getHeroComponent: () => configRef.current?.heroComponent,
			getSettingsPage: () => configRef.current?.settingsPage,
			getGuestComponent: () => configRef.current?.guestComponent,
			getAppBarComponent: () => configRef.current?.appBarComponent,
			getNavigationDrawerComponent: () => configRef.current?.navigationDrawerComponent,
			getFooterComponent: () => configRef.current?.footerComponent,
			getBreadcrumbsComponent: () => configRef.current?.breadcrumbsComponent,
		}),
		[version]
	);

	return <CustomizationsContext.Provider value={value}>{children}</CustomizationsContext.Provider>;
}
